/**
 * Auth failure classification.
 *
 * The auth status union has exactly ONE failure value ('autoJwtFail'), which every
 * failure path collapsed into: no token at all, a token the backend REJECTED, a
 * network error, a 5xx, and the 15s auto-JWT timeout all looked identical to the
 * route guard. That made a transient backend stall indistinguishable from an expired
 * session, so a blip bounced a perfectly valid session to /login.
 *
 * This module preserves the reason at the point where it is still known (the rejected
 * RTK Query error), so the guard can act on it. Pure + dependency-free so it can be
 * unit-tested in the node jest env.
 */

export type AuthFailureKind =
  /** The server judged the credential and said no (401/403). The session IS over. */
  | 'credential-rejected'
  /**
   * The request never produced a credential verdict: network error, timeout, abort,
   * 5xx, or any other non-judging response. The stored token is probably still valid.
   */
  | 'unreachable'
  /** There was no credential to try at all (no stored token, or an explicit logout). */
  | 'no-credential';

const isRejectionStatus = (status: unknown): boolean => status === 401 || status === 403;

interface ErrorLike {
  status?: unknown;
  originalStatus?: unknown;
  error?: { status?: unknown; originalStatus?: unknown };
}

/**
 * Classify a rejected auth attempt.
 *
 * Accepts both shapes we see in practice: the unwrapped `FetchBaseQueryError`
 * ({ status, data }) thrown by `.unwrap()`, and the `{ error: FetchBaseQueryError }`
 * envelope RTK Query rejects `queryFulfilled` with. `PARSING_ERROR` carries the HTTP
 * code on `originalStatus`, so that is checked too.
 *
 * ONLY an explicit 401/403 counts as a rejected credential. Everything else —
 * 'FETCH_ERROR', 'TIMEOUT_ERROR', 'CUSTOM_ERROR', 5xx, other 4xx, our own timeout
 * Error, an AbortError, or anything unrecognised — is 'unreachable'. That default is
 * deliberate and it is the safe side: 'unreachable' never logs anyone out, and the
 * unreachable UI always offers a manual "Sign in" escape hatch, so a MISclassified
 * hard failure costs the user one click. The inverse mistake (calling a blip a
 * rejection) is what this whole change exists to stop.
 */
export const classifyAuthFailure = (error: unknown): AuthFailureKind => {
  const err = (error ?? undefined) as ErrorLike | undefined;
  const status = err?.status ?? err?.error?.status;
  const originalStatus = err?.originalStatus ?? err?.error?.originalStatus;
  if (isRejectionStatus(status) || isRejectionStatus(originalStatus)) return 'credential-rejected';
  return 'unreachable';
};

/**
 * Should a failed auth bounce the visitor to /login?
 *
 * Everything except 'unreachable' does. An UNKNOWN/undefined kind also does — that is
 * the pre-existing behaviour, and it keeps a stale/unclassified failure from stranding
 * someone on a loader with no way forward.
 */
export const shouldRedirectToLoginOnAuthFailure = (kind?: AuthFailureKind): boolean =>
  kind !== 'unreachable';
